import { useEffect, useMemo, useState } from 'react';
import {
  SignupFormState,
  useSignupFormContext,
} from '../../context/useSignUpContext';
import {
  UseFormHandleSubmit,
  UseFormReturn,
  useForm,
} from '@agrotoken-tech/ui';
import { useDebounce, useTranslation } from '@bcba/react-hooks';
import { useQuery } from '@tanstack/react-query';
import { getProvinces } from '../../../../services/provinces/getProvinces';
import {
  isCompanyFiscalIdAvailable,
  isCompanyNameAvailable,
} from '../../../../services/public/isCompanyAvailable';
import { getProvinceLocalities } from '../../../../services/provinces/getLocalities';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { CompanyDataDto } from '../../../../services/sign-up/dto/createSignUpDto';
import { CompanyType, companyTypes } from '@bcba/types';
import { LocalitySerializer } from '@bcba/serializers';
import { FormStep } from '../utils/form-step.interface';
import { getCompanyName } from '../../../../services/sign-up/getCompanyName';
import { LocaleKeys } from '@bcba/translations';

const resolver = classValidatorResolver(CompanyDataDto);

interface Props {
  handleSubmit: UseFormHandleSubmit<SignupFormState, undefined>;
  submitHandler: (data: SignupFormState) => Promise<void>;
  formMethods: UseFormReturn<SignupFormState, any, undefined>;
  dropdownProvincesOptions: DropdownOption[];
  hasSelectedProvince: boolean;
  defaultLocalityValueFromPreviousSelection: any;
  getProvinceLocalitiesOptions: (
    searchValue: string,
  ) => Promise<DropdownOption[]>;
  companyTypesOptions: { value: CompanyType; label: string }[];
  afipResponseIsLoading: boolean;
  companyName: string;
  showCompanyNameFieldReadOnly: boolean;
}

interface DropdownOption {
  label: string;
  value: string;
}

export const useFirstStepForm = (props: FormStep): Props => {
  const { setStep } = props;
  const { formState, updateForm, setIsDirty } = useSignupFormContext();
  const formMethods = useForm({ resolver, defaultValues: formState });
  const { t } = useTranslation();
  const {
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    trigger,
    formState: rhfState,
  } = formMethods;
  const [localities, setLocalities] = useState<DropdownOption[]>([]);
  const companyName = watch('companyName') ?? '';
  const companyProvinceName = watch('companyProvinceName');
  const selectedProvinceUuid = watch('companyProvinceUuid');
  const hasSelectedProvince = Boolean(selectedProvinceUuid);
  const debouncedCompanyFiscalId = useDebounce(watch('companyFiscalId'), 500);
  const [enableAfipQuery, setEnableAfipQuery] = useState(false);

  const { data: provinces = [] } = useQuery({
    queryKey: ['provinces'],
    queryFn: getProvinces,
  });

  const {
    data: afipResponse,
    isLoading: afipResponseIsLoading,
    isError: afipResponseIsError,
    error,
  } = useQuery({
    queryKey: ['company-name', debouncedCompanyFiscalId],
    queryFn: async () => getCompanyName(debouncedCompanyFiscalId ?? ''),
    enabled: !!debouncedCompanyFiscalId && enableAfipQuery,
    retry: false,
  });

  const showCompanyNameFieldReadOnly = Boolean(afipResponse?.name);

  const getProvinceLocalitiesOptions = (searchValue: string) =>
    hasSelectedProvince
      ? getProvinceLocalities(String(selectedProvinceUuid), searchValue).then(
          (res) => {
            const result = res.map((locality: LocalitySerializer) => ({
              label: locality.name,
              value: String(locality.id),
            }));
            setLocalities(result);
            return result;
          },
        )
      : Promise.resolve([]);

  const dropdownProvincesOptions = useMemo(
    () =>
      provinces?.map(({ name, uuid }) => ({
        label: name,
        value: uuid,
      })) ?? [],
    [provinces],
  );

  const submitHandler = async (data: SignupFormState) => {
    delete data.companyProvinceUuid;

    const isValidFiscalId = await isCompanyFiscalIdAvailable(
      Number(data.companyFiscalId),
    );

    const isValidCompanyName = await isCompanyNameAvailable(
      String(data.companyName),
    );
    if (!isValidFiscalId) {
      setError('companyFiscalId', {
        type: 'custom',
        message: t('businessSignUp.firstStep.error.companyAlreadyExist'),
      });
      return;
    } else {
      clearErrors('companyFiscalId');
    }

    if (!isValidCompanyName) {
      setError('companyName', {
        type: 'custom',
        message: t('businessSignUp.firstStep.error.companyNameAlreadyExist'),
      });
      return;
    } else {
      clearErrors('companyName');
    }

    const locality = localities.find(
      (locality) => String(locality.value) === String(data.companyLocalityId),
    );
    const companyLocalityName = locality?.label ?? watch('companyLocalityName');

    updateForm({
      formState: {
        ...formState,
        ...data,
        companyLocalityName,
      },
    });
    setStep(2);
  };

  const companyTypesOptions = companyTypes.map((value) => {
    return {
      value,
      label: t(`businessSignup.form.companyType.${value}`),
    };
  });

  useEffect(() => {
    if (!!debouncedCompanyFiscalId) {
      trigger('companyFiscalId').then((isValid) => {
        setEnableAfipQuery(isValid);
      });
    }
  }, [debouncedCompanyFiscalId, trigger]);

  useEffect(() => {
    if (afipResponseIsError) {
      const errorResponse = error as any;
      const errorCode = errorResponse?.code;
      setError('companyFiscalId', {
        message: t(`error.${errorCode}.errorDescription` as LocaleKeys),
      });
    } else {
      clearErrors('companyFiscalId');
    }
    if (afipResponse) {
      setValue('companyName', afipResponse?.name, { shouldValidate: false });
    } else {
      setValue('companyName', '');
    }
  }, [afipResponse, afipResponseIsError]);

  useEffect(() => {
    if (Object.keys(rhfState.dirtyFields).length > 0) {
      setIsDirty(true);
    }
  }, [rhfState.dirtyFields, setIsDirty]);

  useEffect(() => {
    if (!selectedProvinceUuid && formMethods.formState.isSubmitted) {
      setValue('companyProvinceId', undefined);
      setValue('companyProvinceName', '');
      setError('companyProvinceUuid', {
        type: 'required',
        message: t('class-validator.isNotEmpty.error'),
      });
    }
    if (selectedProvinceUuid) {
      const selectedProvince = provinces.find(
        ({ uuid }) => uuid === selectedProvinceUuid,
      );
      const selectedProvinceId = selectedProvince?.id;
      const selectedProvinceName = selectedProvince?.name;

      setValue('companyProvinceId' as any, String(selectedProvinceId || ''));
      setValue('companyProvinceName' as any, selectedProvinceName || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvinceUuid, formMethods.formState.isSubmitted]);

  useEffect(() => {
    if (companyProvinceName) {
      const selectedProvince = provinces.find(
        ({ name }) => name === companyProvinceName,
      );

      updateForm({
        formState: {
          ...formState,
          companyProvinceUuid: selectedProvince?.uuid,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyProvinceName]);

  useEffect(() => {
    if (selectedProvinceUuid && formMethods.formState.isDirty) {
      setError('companyLocalityId', {
        type: 'custom',
        message: t('companyContactInformation.form.error.validLocality'),
      });
      setValue('companyLocalityId', undefined);
      setValue('companyLocalityName', '');
    }
    setLocalities([]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvinceUuid]);

  // Since localities values comes async, we created an option element and pass it down to the SelectField
  // This way we can pre-write the selected value when the user navigates back to first step (without doing the same request)
  const defaultLocalityValueFromPreviousSelection =
    formState.companyLocalityName
      ? [
          {
            label: formState.companyLocalityName,
            value: formState.companyLocalityId,
          },
        ]
      : [];

  return {
    handleSubmit,
    submitHandler,
    formMethods,
    dropdownProvincesOptions,
    hasSelectedProvince,
    getProvinceLocalitiesOptions,
    companyTypesOptions,
    defaultLocalityValueFromPreviousSelection,
    afipResponseIsLoading,
    companyName,
    showCompanyNameFieldReadOnly,
  };
};

export default useFirstStepForm;
